import { useCallback, useState } from 'react';
import {
  createDefaultFetch,
  createFetchedSuccess,
  createFetchedWithError,
  createFetchInProgress,
  IFetchStatus,
} from 'types/rest/IFetchStatus';
import { IFetchDataState } from '../../../store/IFetchDataState';

export const useAsyncOperationWithData = <Query, Data>(
  action: (x: Query) => Promise<Data>
): [IFetchDataState<Query | undefined, Data | undefined>, (x: Query) => Promise<void>, () => void] => {
  const [query, setQuery] = useState<Query | undefined>(undefined);
  const [fetchStatus, setFetchStatus] = useState<IFetchStatus>(createDefaultFetch());
  const [fetchError, setFetchError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<Data | undefined>(undefined);

  const loadAsyncDataAction = useCallback(
    async (newQuery: Query) => {
      setFetchStatus(createFetchInProgress());
      setQuery(newQuery);
      try {
        const newData = await action(newQuery);
        setData(newData);
        setFetchError(undefined);
        setFetchStatus(createFetchedSuccess());
      } catch (e: any) {
        setQuery(newQuery);
        setFetchError(e);
        setData(undefined);
        setFetchStatus(createFetchedWithError());
      }
    },
    [action]
  );
  const reset = useCallback(() => {
    setData(undefined);
    setFetchError(undefined);
    setFetchStatus(createDefaultFetch());
    setQuery(undefined);
  }, [setData, setFetchError, setFetchStatus, setQuery]);

  return [{ fetchStatus, fetchError, data, query }, loadAsyncDataAction, reset];
};
